import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { useTheme } from "@mui/material";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { type TierType } from "@src/appV2/redesign/Tiers/api/schema";
import { useTiersBasketSize } from "@src/appV2/redesign/Tiers/api/useTiersBasketSize";
import { BasePerk } from "@src/appV2/redesign/Tiers/PerksSection/BasePerk";
import { BasePerksSection } from "@src/appV2/redesign/Tiers/PerksSection/BasePerksSection";
import { getTextColorForTier } from "@src/appV2/redesign/Tiers/utils/getTextColorForTier";
import { capitalize } from "lodash";
import pluralize from "pluralize";

interface LimitedPerksSectionProps {
  workerTier: TierType;
}

export function LimitedPerksSection(props: LimitedPerksSectionProps) {
  const { workerTier } = props;
  const theme = useTheme();
  const { leadTimeThresholdInHrs, basketSize, basketSizeWord } = useTiersBasketSize();
  const matchesWorkerTier = workerTier === "LIMITED";
  const isLimitedTierUnassignmentJobEnabled = useCbhFlag(
    CbhFeatureFlag.ENABLE_LIMITED_TIER_UNASSIGNMENT_JOB,
    { defaultValue: false }
  );
  const unassignmentCopy = isLimitedTierUnassignmentJobEnabled
    ? `. You'll be unassigned from all others except your next ${basketSizeWord}`
    : "";

  return (
    <BasePerksSection
      headerText={
        <Text semibold variant="h4" color={getTextColorForTier("LIMITED", theme)}>
          Limited
        </Text>
      }
      icon={
        <CbhIcon type="membership-limited-colored" size="large" sx={{ transform: "scale(1.2)" }} />
      }
      perks={[
        <BasePerk
          key="last-minute-bookings"
          includeDividerBelow
          illustration="tiers-clock"
          title="Last-minute bookings only"
          description={`You can only book shifts that start within the next ${leadTimeThresholdInHrs} ${pluralize(
            "hour",
            leadTimeThresholdInHrs
          )}`}
        />,
        <BasePerk
          key="one-shift-at-a-time"
          illustration="block-calendar-with-diamond"
          title={`${capitalize(basketSizeWord)} ${pluralize("shift", basketSize)} at a time`}
          description={`You can only have ${basketSizeWord} upcoming ${pluralize(
            "shift",
            basketSize
          )} at a time${unassignmentCopy}`}
        />,
      ]}
      matchesWorkerTier={matchesWorkerTier}
    />
  );
}
